/**
 * Путь человека: миссии, опыт и уровни.
 *
 * Миссия — мини-обучение делом: не «прочитай, что такое скринер», а
 * «загляни в радар». Ничего не закрыто — ни один инструмент не ждёт
 * миссии или уровня. Миссия засчитывается сама, когда человек сделал
 * дело любым путём: собрал бота из рецепта или из мастерской — всё
 * равно «первый бот».
 *
 * Опыт — за разнообразие, а не за количество: каждый значок и каждая
 * миссия считаются один раз. Сто одинаковых проверок уровня не дадут.
 * Опыт не хранится, а считается из значков и миссий — подделать его
 * правкой одного числа нельзя, и он не разойдётся с тем, что видно.
 */

import { experienceOf, levelFrom, MISSION_XP, titleFor, type JourneyPath } from '@specter/types';
import type { IconName } from '../ui/NeonIcon.js';
import type { TerminalState } from '../state/store.js';

export interface Mission {
  readonly id: string;
  readonly title: string;
  /** Чему учит — одной фразой. */
  readonly teach: string;
  /** Что сделать — подсказка на экране, пока миссия идёт. */
  readonly how: string;
  readonly xp: number;
  readonly icon: IconName;
  readonly tone: string;
  /** Куда вести: раздел и что там открыть. */
  readonly go: { readonly section: 'chart' | 'radar'; readonly open?: 'studio' | 'bots' };
  /** Сделано ли — по состоянию, а не по нажатию «готово». */
  done(state: TerminalState): boolean;
}

const has = (state: TerminalState, badge: string): boolean => state.studioProgress.badges.includes(badge);
const passed = (state: TerminalState, id: string): boolean => state.journey.missions.some((mark) => mark.id === id);

export const MISSIONS: readonly Mission[] = [
  {
    id: 'move',
    title: 'Найди сильное движение',
    teach: 'Отличать обычную свечу от необычной — с этого начинается любой сигнал.',
    how: 'Найдите на графике свечу, где цена прошла заметно больше обычного, и нажмите на неё.',
    xp: 100,
    icon: 'candle',
    tone: '#2ee6b0',
    go: { section: 'chart' },
    done: (state) => passed(state, 'move'),
  },
  {
    id: 'radar',
    title: 'Загляни в радар',
    teach: 'Радар сам ищет пампы, дампы и необычный объём по всему рынку.',
    how: 'Откройте раздел «Радар» и нажмите на любую монету на карте.',
    xp: 100,
    icon: 'compass',
    tone: '#67e8f9',
    go: { section: 'radar' },
    done: (state) => has(state, 'radar-first'),
  },
  {
    id: 'alert',
    title: 'Поставь оповещение',
    teach: 'Не сидеть у графика: платформа сама скажет, когда цена дойдёт до уровня.',
    how: 'Нажмите на колокольчик справа от графика и поставьте оповещение на цену.',
    xp: 100,
    icon: 'bell',
    tone: '#f5c04e',
    go: { section: 'chart' },
    done: (state) => state.priceAlerts.length > 0,
  },
  {
    id: 'idea',
    title: 'Изучи идею из библиотеки',
    teach: 'Восемьдесят готовых торговых идей — у каждой паспорт: где работает и где нет.',
    how: 'Откройте «Индикаторы» → «Создать» и загляните в любую идею библиотеки.',
    xp: 100,
    icon: 'library',
    tone: '#a78bfa',
    go: { section: 'chart', open: 'studio' },
    done: (state) => state.studioProgress.explored.length > 0,
  },
  {
    id: 'indicator',
    title: 'Собери свой индикатор',
    teach: 'Мысль «когда средние пересеклись и RSI выше 55» превращается в сигнал на графике.',
    how: 'В студии индикаторов соберите сигнал из блоков или словами и сохраните его.',
    xp: 150,
    icon: 'studio',
    tone: '#a78bfa',
    go: { section: 'chart', open: 'studio' },
    done: (state) => has(state, 'first') || state.specs.length > 0,
  },
  {
    id: 'strategy',
    title: 'Проверь идею как стратегию',
    teach: 'Сигнал — ещё не стратегия: нужны вход, выход и стоп. Проверка на истории покажет, как это торговалось бы.',
    how: 'В студии нажмите «Проверить как стратегию» у своего индикатора.',
    xp: 150,
    icon: 'history',
    tone: '#fb923c',
    go: { section: 'chart', open: 'studio' },
    done: (state) => has(state, 'strategy'),
  },
  {
    id: 'bot',
    title: 'Запусти бота',
    teach: 'Бот торгует сам на виртуальном счёте — круглосуточно, даже когда вкладка закрыта.',
    how: 'Нажмите «Боты» над графиком, выберите героя и выпустите его.',
    xp: 150,
    icon: 'bot',
    tone: '#fb923c',
    go: { section: 'chart', open: 'bots' },
    done: (state) => has(state, 'bot-first') || state.customBots.length > 0,
  },
  {
    id: 'guard',
    title: 'Защити бота',
    teach: 'Безубыток и скользящий стоп не дают отдать рынку уже заработанное.',
    how: 'В мастерской бота на шаге «Выход и защита» включите безубыток или скользящий стоп.',
    xp: 100,
    icon: 'structure',
    tone: '#34d399',
    go: { section: 'chart', open: 'bots' },
    done: (state) => has(state, 'bot-guard'),
  },
  {
    id: 'my-radar',
    title: 'Собери свой радар',
    teach: 'Свой фильтр по всему рынку: сервер проверяет его раз в минуту и пишет, когда монета попала.',
    how: 'В «Радаре» откройте «Мои радары» и возьмите любую заготовку.',
    xp: 100,
    icon: 'map',
    tone: '#67e8f9',
    go: { section: 'radar' },
    done: (state) => state.radars.length > 0,
  },
  {
    id: 'scan',
    title: 'Прогони идею по рынку',
    teach: 'Одна идея — сто монет: где отметка стоит прямо сейчас.',
    how: 'В «Радаре» откройте «Скан по идеям» и нажмите «Прогнать».',
    xp: 100,
    icon: 'globe',
    tone: '#a78bfa',
    go: { section: 'radar' },
    done: (state) => has(state, 'radar-scan'),
  },
];

/** Порядок миссий по выбранному началу. Всё открыто — меняется только, что показать первым. */
const ORDER: Record<JourneyPath, readonly string[]> = {
  trader: ['move', 'alert', 'radar', 'idea', 'indicator', 'bot', 'my-radar', 'scan', 'strategy', 'guard'],
  explorer: ['move', 'radar', 'my-radar', 'scan', 'alert', 'idea', 'indicator', 'bot', 'strategy', 'guard'],
  creator: ['move', 'idea', 'indicator', 'strategy', 'radar', 'bot', 'alert', 'my-radar', 'scan', 'guard'],
  builder: ['move', 'bot', 'guard', 'idea', 'indicator', 'strategy', 'radar', 'my-radar', 'alert', 'scan'],
};

export function missionsFor(path: JourneyPath | null): Mission[] {
  const order = ORDER[path ?? 'trader'];
  return order.map((id) => MISSIONS.find((mission) => mission.id === id)!).filter(Boolean);
}

export function missionById(id: string | null): Mission | null {
  return MISSIONS.find((mission) => mission.id === id) ?? null;
}

/* ------------------------------------------------------------ опыт и уровни */

/*
 * Опыт и уровни считаются в общем пакете: сервер показывает уровень в
 * публичном профиле тем же расчётом, что терминал — на Главной.
 */
export { badgeXp, levelFloor } from '@specter/types';

export function xpOf(state: Pick<TerminalState, 'studioProgress' | 'journey'>): number {
  return experienceOf(
    state.studioProgress.badges,
    state.journey.missions.map((mark) => mark.id),
  );
}

export function levelOf(xp: number): { level: number; floor: number; next: number } {
  return levelFrom(xp);
}

export function titleOf(level: number): string {
  return titleFor(level);
}

export const PATHS: ReadonlyArray<{ readonly id: JourneyPath; readonly title: string; readonly text: string; readonly icon: IconName; readonly tone: string }> = [
  { id: 'trader', title: 'Трейдер', text: 'Торговать руками: график, уровни, оповещения о цене.', icon: 'price', tone: '#f5c04e' },
  { id: 'explorer', title: 'Исследователь', text: 'Искать возможности: радар, пампы, необычные монеты.', icon: 'compass', tone: '#2ee6b0' },
  { id: 'creator', title: 'Создатель индикаторов', text: 'Собирать свои сигналы из блоков и проверять их на истории.', icon: 'studio', tone: '#a78bfa' },
  { id: 'builder', title: 'Строитель ботов', text: 'Собирать ботов, которые торгуют сами, пока вас нет.', icon: 'bot', tone: '#fb923c' },
];
